// See TimePicklist.spec.md for TPL-FR-001 and TPL-FR-002
//
// The single place that defines the time ranges ("Last 1 Hour", "Last 7 Days", ...) and turns one
// into a start/end window. TimePicklist only lets the user pick a range; pages call getTimeRange
// when they fetch data, so every fetch (including refreshes) uses the current time.

export const TIME_RANGES = {
  last_5_minutes: 'last_5_minutes',
  last_15_minutes: 'last_15_minutes',
  last_30_minutes: 'last_30_minutes',
  last_1_hour: 'last_1_hour',
  last_3_hours: 'last_3_hours',
  last_5_hours: 'last_5_hours',
  last_6_hours: 'last_6_hours',
  last_12_hours: 'last_12_hours',
  last_24_hours: 'last_24_hours',
  last_48_hours: 'last_48_hours',
  last_7_days: 'last_7_days',
  last_30_days: 'last_30_days',
  last_60_days: 'last_60_days',
} as const

export type TimeRange = (typeof TIME_RANGES)[keyof typeof TIME_RANGES]

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

// TPL-FR-001: Label and length of each range
const TIME_RANGE_DETAILS: Record<TimeRange, { label: string; durationMs: number }> = {
  last_5_minutes: { label: 'Last 5 Minutes', durationMs: 5 * MINUTE_MS },
  last_15_minutes: { label: 'Last 15 Minutes', durationMs: 15 * MINUTE_MS },
  last_30_minutes: { label: 'Last 30 Minutes', durationMs: 30 * MINUTE_MS },
  last_1_hour: { label: 'Last 1 Hour', durationMs: HOUR_MS },
  last_3_hours: { label: 'Last 3 Hours', durationMs: 3 * HOUR_MS },
  last_5_hours: { label: 'Last 5 Hours', durationMs: 5 * HOUR_MS },
  last_6_hours: { label: 'Last 6 Hours', durationMs: 6 * HOUR_MS },
  last_12_hours: { label: 'Last 12 Hours', durationMs: 12 * HOUR_MS },
  last_24_hours: { label: 'Last 24 Hours', durationMs: 24 * HOUR_MS },
  last_48_hours: { label: 'Last 48 Hours', durationMs: 48 * HOUR_MS },
  last_7_days: { label: 'Last 7 Days', durationMs: 7 * DAY_MS },
  last_30_days: { label: 'Last 30 Days', durationMs: 30 * DAY_MS },
  last_60_days: { label: 'Last 60 Days', durationMs: 60 * DAY_MS },
}

// TPL-FR-001: Ranges TimePicklist shows when a page doesn't choose its own (Last 5 Hours is opt-in)
export const DEFAULT_TIME_RANGE_OPTIONS: TimeRange[] = [
  TIME_RANGES.last_5_minutes,
  TIME_RANGES.last_15_minutes,
  TIME_RANGES.last_30_minutes,
  TIME_RANGES.last_1_hour,
  TIME_RANGES.last_3_hours,
  TIME_RANGES.last_6_hours,
  TIME_RANGES.last_12_hours,
  TIME_RANGES.last_24_hours,
  TIME_RANGES.last_48_hours,
  TIME_RANGES.last_7_days,
  TIME_RANGES.last_30_days,
  TIME_RANGES.last_60_days,
]

export const getTimeRangeLabel = (timeRange: TimeRange): string =>
  TIME_RANGE_DETAILS[timeRange].label

// TPL-FR-002: Window for a range, in epoch seconds: it ends now and starts `duration` earlier
export const getTimeRange = (
  timeRange: TimeRange,
  nowMs: number = Date.now(),
): { start: number; end: number } => {
  const details = TIME_RANGE_DETAILS[timeRange]
  if (!details) throw new Error(`Unknown time range: ${timeRange}`)
  return {
    start: Math.floor((nowMs - details.durationMs) / 1000),
    end: Math.floor(nowMs / 1000),
  }
}
